import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useCollection, PageHeader, CrudActions, Modal, Empty } from "../components/Crud";
import BreakdownChip from "../components/BreakdownChip";
import ProjectBreakdown from "../components/ProjectBreakdown";
import DatePicker, { DateTimePicker } from "../components/DatePicker";
import { clientDisplayName } from "../lib/clientName";
import { Plus, FolderKanban, ExternalLink, ChevronDown, ChevronRight } from "lucide-react";

export default function Projects() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const isPro = user?.role === "professional";
    const { items, create, update, remove } = useCollection("projects");
    const [clients, setClients] = useState([]);
    const [classes, setClasses] = useState([]);
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [q, setQ] = useState("");
    const [groupOn, setGroupOn] = useState(false);
    const [filterParent, setFilterParent] = useState("");
    const blank = { title: "", description: "", start_date: "", end_date: "", client_id: "", client_name: "", class_id: "", class_name: "", progress: 0 };
    const [form, setForm] = useState(blank);

    useEffect(() => {
        if (isPro) api.get("/clients").then(r => setClients(r.data.filter(c => !c.archived)));
        else api.get("/classes").then(r => setClasses(r.data.filter(c => !c.archived)));
    }, [isPro]);

    const parentIdKey = isPro ? "client_id" : "class_id";
    const parentNameKey = isPro ? "client_name" : "class_name";
    const parentLabel = isPro ? "client" : "class";
    const parentList = isPro ? clients : classes;
    const parentDisplay = (p) => isPro ? clientDisplayName(p) : p.name;
    const parentLabelPlural = isPro ? "clients" : "classes";

    const matchesSearch = (p) => `${p.title} ${p.client_name || ""} ${p.class_name || ""}`.toLowerCase().includes(q.toLowerCase());
    const matchesParent = (p) => !filterParent || p[parentIdKey] === filterParent;
    const filtered = items.filter(p => matchesSearch(p) && matchesParent(p));

    const grouped = groupOn
        ? filtered.reduce((acc, p) => {
            const k = p[parentNameKey] || "Unassigned";
            (acc[k] = acc[k] || []).push(p); return acc;
        }, {}) : null;

    const submit = async (e) => {
        e.preventDefault();
        const parent = parentList.find(c => c.id === form[parentIdKey]);
        const payload = { ...form, [parentNameKey]: parent ? parentDisplay(parent) : "" };
        if (edit) await update(edit.id, payload); else await create(payload);
        setOpen(false); setEdit(null); setForm(blank);
    };

    return (
        <div data-testid="projects-page">
            <PageHeader title={t("pages.projectsTitle")} subtitle={t("pages.projectsEyebrow")}
                action={<button onClick={() => { setEdit(null); setForm(blank); setOpen(true); }}
                                data-testid="add-project-btn" className="btn-primary inline-flex items-center gap-2"><Plus size={16}/> {t("pages.projectsAdd")}</button>} />
            <div className="flex flex-wrap items-center gap-2 mb-5">
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`${t("common.search")}…`}
                       data-testid="projects-search"
                       className="flex-1 min-w-[14rem] max-w-sm px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" />
                <select value={filterParent} onChange={(e) => setFilterParent(e.target.value)}
                        data-testid={`projects-filter-${parentLabel}`}
                        className="px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] text-sm">
                    <option value="">{t("pages.projectsFilterAll")} {parentLabelPlural}</option>
                    {parentList.map(c => <option key={c.id} value={c.id}>{parentDisplay(c)}</option>)}
                </select>
                <button onClick={() => setGroupOn(g => !g)} data-testid="projects-group-toggle"
                        className={`text-sm px-3 py-1.5 rounded-full ${groupOn ? "brand-gradient text-white" : "btn-ghost"}`}>
                    {groupOn ? t("common.close") : t("pages.projectsGroupByClient")}
                </button>
            </div>
            {filtered.length === 0 ? (
                <Empty icon={FolderKanban} title={t("pages.projectsEmpty")} hint={t("pages.projectsEmptyHint")} />
            ) : (
                <>
                {grouped ? (
                    <div className="space-y-6" data-testid="projects-grouped">
                        {Object.entries(grouped).map(([parentName, list]) => (
                            <section key={parentName} data-testid={`group-${parentName}`}>
                                <div className="flex items-baseline gap-3 mb-2">
                                    <div className="font-script text-[var(--brand-orange)] text-lg">{parentName === "Unassigned" ? `no ${parentLabel}` : parentLabel}</div>
                                    <h2 className="font-serif text-2xl">{parentName}</h2>
                                    <span className="text-xs text-[var(--text-ink)]/55">{list.length} project{list.length === 1 ? "" : "s"}</span>
                                </div>
                                <ProjectGrid list={list} blank={blank} setEdit={setEdit} setForm={setForm} setOpen={setOpen} update={update} remove={remove} />
                            </section>
                        ))}
                    </div>
                ) : (
                    <ProjectGrid list={filtered} blank={blank} setEdit={setEdit} setForm={setForm} setOpen={setOpen} update={update} remove={remove} />
                )}
                </>
            )}
            <Modal open={open} onClose={() => setOpen(false)} title={edit ? t("common.edit") : t("pages.projectsAdd")} size={edit ? "3xl" : "lg"}>
                <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3" data-testid="project-form">
                    <label className="text-sm sm:col-span-2">Title *
                        <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="project-input-title" className="w-full mt-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" /></label>
                    {isPro ? (
                        <label className="text-sm sm:col-span-2">Client
                            <select value={form.client_id || ""} onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                                    data-testid="project-input-client"
                                    className="w-full mt-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] bg-white/60">
                                <option value="">— No client —</option>
                                {clients.map(c => <option key={c.id} value={c.id}>{clientDisplayName(c)}</option>)}
                            </select>
                        </label>
                    ) : (
                        <label className="text-sm sm:col-span-2">Class / Module
                            <select value={form.class_id || ""} onChange={(e) => setForm({ ...form, class_id: e.target.value })}
                                    data-testid="project-input-class"
                                    className="w-full mt-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] bg-white/60">
                                <option value="">— No class —</option>
                                {classes.map(c => <option key={c.id} value={c.id}>{c.code ? `${c.code} · ` : ""}{c.name}{c.lecturer_name ? ` — ${c.lecturer_name}` : ""}</option>)}
                            </select>
                        </label>
                    )}
                    <label className="text-sm sm:col-span-2">Description
                        <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)]" /></label>
                    <label className="text-sm">Start
                        <div className="mt-1">
                            <DatePicker value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })}
                                        testid="project-input-start" placeholder="Start date" />
                        </div></label>
                    <label className="text-sm">End (to the hour)
                        <div className="mt-1">
                            <DateTimePicker value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })}
                                            testid="project-input-end" placeholder="Due (date & time)" />
                        </div></label>
                    <div className="sm:col-span-2 text-xs text-[var(--text-ink)]/55 px-1">
                        Progress is calculated automatically from the tasks you add inside this project.
                    </div>
                    <div className="sm:col-span-2 flex justify-end gap-2">
                        <button type="button" onClick={() => setOpen(false)} className="btn-ghost">{t("common.cancel")}</button>
                        <button type="submit" data-testid="project-save-btn" className="btn-primary">{t("common.save")}</button>
                    </div>
                </form>
                {edit?.id && (
                    <div className="mt-6 pt-5 border-t border-[rgba(26,19,24,0.08)]" data-testid="modal-breakdown">
                        <div className="flex items-baseline gap-2 mb-2">
                            <div className="font-script text-[var(--brand-orange)] text-lg">quick capture</div>
                            <h3 className="font-serif text-xl">Tasks &amp; milestones</h3>
                        </div>
                        <p className="text-xs text-[var(--text-ink)]/55 mb-3">Add and tick off tasks right here — progress updates automatically when you close the modal.</p>
                        <ProjectBreakdown scope="project" scopeId={edit.id} />
                    </div>
                )}
            </Modal>
        </div>
    );
}

function ProjectGrid({ list, blank, setEdit, setForm, setOpen, update, remove }) {
    return (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {list.map(p => (
                <ProjectCard key={p.id} p={p} update={update} remove={remove}
                             onEdit={() => { setEdit(p); setForm({ ...blank, ...p }); setOpen(true); }} />
            ))}
        </div>
    );
}

function ProjectCard({ p, update, remove, onEdit }) {
    const [expanded, setExpanded] = useState(false);
    const parentLink = p.client_id ? `/clients/${p.client_id}` : (p.class_id ? `/classes/${p.class_id}` : null);
    const parentName = p.client_name || p.class_name;
    return (
        <div className={`card-warm p-5 ${p.archived ? "opacity-60" : ""}`} data-testid={`project-card-${p.id}`}>
            <div className="flex justify-between items-start gap-2">
                <div className="flex-1 min-w-0">
                    <Link to={`/projects/${p.id}`} data-testid={`open-project-${p.id}`} className="block">
                        <div className="font-serif text-xl truncate hover:text-[var(--brand-purple)] transition">{p.title}</div>
                    </Link>
                    {parentName ? (
                        parentLink ? (
                            <Link to={parentLink}
                                  data-testid={`open-parent-${p.id}`}
                                  title={`Open ${p.client_id ? "client" : "class"}`}
                                  className="inline-flex items-center gap-1 mt-1 text-xs text-[var(--text-ink)]/60 hover:text-[var(--brand-orange)] transition group">
                                <span className="truncate max-w-[14rem]">{parentName}</span>
                                <ExternalLink size={12} className="opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-transform" />
                            </Link>
                        ) : (
                            <div className="text-xs text-[var(--text-ink)]/60 mt-1">{parentName}</div>
                        )
                    ) : (
                        <div className="text-xs text-[var(--text-ink)]/40 mt-1">— no {p.client_id !== undefined ? "client" : "class"} —</div>
                    )}
                </div>
                <CrudActions idBase={`prj-${p.id}`}
                    onEdit={onEdit}
                    onArchive={() => update(p.id, { archived: !p.archived })}
                    onDelete={() => window.confirm("Delete project?") && remove(p.id)}
                    archived={p.archived} />
            </div>
            <div className="mt-3">
                <BreakdownChip scope="project" id={p.id} testidSuffix={`prj-${p.id}`} />
                <div className="text-[10px] text-[var(--text-ink)]/45 mt-1">ends {p.end_date || "—"}</div>
            </div>
            <button onClick={() => setExpanded(!expanded)}
                    data-testid={`project-expand-${p.id}`}
                    className="mt-3 inline-flex items-center gap-1 text-xs text-[var(--brand-purple)] hover:text-[var(--brand-magenta)]">
                {expanded ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                {expanded ? "Hide breakdown" : "Show breakdown"}
            </button>
            {expanded && (
                <div className="mt-3 border-t border-[rgba(26,19,24,0.06)] pt-3" data-testid={`project-breakdown-inline-${p.id}`}>
                    <ProjectBreakdown scope="project" scopeId={p.id} />
                </div>
            )}
        </div>
    );
}
